import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Notice, Spinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { relativeDate } from '../../lib/school';
import {
  buildRequest,
  createFramework,
  getAllRequests,
  getLibrary,
  getRequest,
  getSubmissions,
  publishBuiltFramework,
  STATUS_LABEL,
  updateRequest,
  type FrameworkRequest,
  type RequestStatus,
} from '../../lib/frameworks';
import { FrameworkTabs } from './FrameworkTabs';
import { LeaderMessagePreview } from './LeaderMessagePreview';
import { FrameworkBuildReview, isBuilding } from './FrameworkBuildReview';
import '../Page.css';
import './Admin.css';

const FILTERS: { value: '' | RequestStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'requested', label: 'New' },
  { value: 'in_progress', label: 'Being built' },
  { value: 'needs_info', label: 'Asked for more' },
  { value: 'ready', label: 'Ready' },
  { value: 'declined', label: 'Declined' },
];

/**
 * Everything waiting on an admin: frameworks leaders asked for (built and
 * checked by AI, then published, edited or sent back for a bit more), plus
 * any framework a school built itself in an older version of the app and
 * sent for review (opens in the editor).
 */
export function AdminFrameworkRequestsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'' | RequestStatus>('');
  const list = useQuery({ queryKey: ['admin-fw-requests', filter], queryFn: () => getAllRequests(filter || undefined) });
  const submissions = useQuery({ queryKey: ['admin-fw-submissions'], queryFn: getSubmissions });
  const rows = list.data ?? [];
  // School-built frameworks count as new until an admin decides.
  const built = filter === '' || filter === 'requested' ? (submissions.data ?? []) : [];

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">EducAitor admin</p>
        <h1>Framework library</h1>
        <p className="muted">
          Leaders ask for a framework on the web and AI builds and checks a draft. Review it and publish: the organisation gets it and the leader is told by
          email and on their phone.
        </p>
      </header>
      <FrameworkTabs />
      <div className="chips" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <button key={f.value} type="button" className="choice-chip" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>
      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <Notice tone="error">Couldn&rsquo;t load requests.</Notice>
      ) : rows.length === 0 && built.length === 0 ? (
        <div className="panel muted">No requests here.</div>
      ) : (
        <div className="req-grid">
          <div className="panel">
            <div className="list">
              {built.map((b) => (
                <button key={b.id} type="button" className="list-row req-item" onClick={() => navigate(`/admin/frameworks/${b.id}`)}>
                  <div className="list-main">
                    <p className="list-title">{b.name}</p>
                    <p className="list-sub">
                      {b.school_name ?? 'A school'} · {relativeDate(b.updated_at)}
                    </p>
                  </div>
                  <span className="chip chip-accent">Built by the school</span>
                </button>
              ))}
              {rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="list-row req-item"
                  aria-current={r.id === id}
                  onClick={() => navigate(`/admin/framework-requests/${r.id}`)}
                >
                  <div className="list-main">
                    <p className="list-title">{r.name}</p>
                    <p className="list-sub">
                      {r.schoolName} · {relativeDate(r.createdAt)}
                    </p>
                  </div>
                  <RequestChip request={r} />
                </button>
              ))}
            </div>
          </div>
          {id ? <RequestDetail id={id} /> : <div className="panel muted">Choose a request to see it.</div>}
        </div>
      )}
    </div>
  );
}

/** Where a request is, from an admin's point of view: whether a build is waiting for them. */
function RequestChip({ request: r }: { request: FrameworkRequest }) {
  let label = STATUS_LABEL[r.status];
  let accent = r.status === 'requested';
  if (r.status === 'in_progress' && r.buildStage) {
    if (r.buildStage === 'ready') [label, accent] = ['Ready to review', true];
    else if (r.buildStage === 'needs_info') [label, accent] = ['Needs more', true];
    else if (r.buildStage === 'failed') [label, accent] = ['Build stopped', true];
    else label = 'Building';
  }
  return <span className={`chip${accent ? ' chip-accent' : ''}`}>{label}</span>;
}

function RequestDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const req = useQuery({
    queryKey: ['admin-fw-request', id],
    queryFn: () => getRequest(id),
    // Follow a build while it runs.
    refetchInterval: (q) => (isBuilding(q.state.data?.build) ? 3000 : false),
    refetchIntervalInBackground: true,
  });
  const library = useQuery({ queryKey: ['admin-frameworks'], queryFn: getLibrary });
  const [frameworkId, setFrameworkId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const building = isBuilding(req.data?.build);

  useEffect(() => {
    setFrameworkId(req.data?.frameworkId ?? '');
    setNote(req.data?.adminNote ?? '');
  }, [req.data?.frameworkId, req.data?.adminNote]);
  useEffect(() => setMessage(null), [id]);
  // When a build finishes, the list's chip changes too.
  useEffect(() => {
    if (!building) qc.invalidateQueries({ queryKey: ['admin-fw-requests'] });
  }, [building, qc]);

  const act = async (fn: () => Promise<unknown>, success: string | null) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await Promise.all([qc.invalidateQueries({ queryKey: ['admin-fw-request', id] }), qc.invalidateQueries({ queryKey: ['admin-fw-requests'] })]);
      if (success) setMessage({ tone: 'success', text: success });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  if (req.isLoading) return <Spinner />;
  if (req.isError || !req.data) return <Notice tone="error">Couldn&rsquo;t load this request.</Notice>;
  const r: FrameworkRequest = req.data;
  const published = (library.data ?? []).filter((f) => f.status === 'approved');
  const closed = r.status === 'ready' || r.status === 'declined';

  const buildYourself = () =>
    act(async () => {
      const { id: fwId } = await createFramework(r.name, r.purpose ?? '');
      await updateRequest(r.id, { status: 'in_progress', frameworkId: fwId });
      navigate(`/admin/frameworks/${fwId}`);
    }, null);

  return (
    <section className="panel form-section">
      <div>
        <p className="eyebrow">
          {r.schoolName} · {STATUS_LABEL[r.status]}
        </p>
        <h2>{r.name}</h2>
        <p className="muted">
          Asked by {r.requestedBy ?? 'a leader'} {relativeDate(r.createdAt).toLowerCase()}.
        </p>
      </div>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      {r.status === 'declined' ? null : (
        <FrameworkBuildReview
          request={r}
          busy={busy || building}
          onBuild={() => act(() => buildRequest(r.id), null)}
          onPublish={() => act(() => publishBuiltFramework(r.id), 'Published. The organisation has it and the leader has been told.')}
          onAsk={(text) => act(() => updateRequest(r.id, { status: 'needs_info', adminNote: text }), 'Sent. The leader has been asked for a bit more.')}
        />
      )}
      {r.status === 'needs_info' && r.adminNote ? (
        <p className="muted">
          <strong>You asked:</strong> {r.adminNote}
        </p>
      ) : null}

      <details className="req-fold">
        <summary>What they sent</summary>
        <div className="req-fold-body">
          {r.purpose ? (
            <p>
              <strong>Who it&rsquo;s for:</strong> {r.purpose}
            </p>
          ) : null}
          {r.fileUrl ? (
            <a className="btn btn-secondary btn-md" href={r.fileUrl}>
              Download {r.fileName ?? 'the document'}
            </a>
          ) : r.fileDeleted ? (
            <p className="muted">{r.fileName ?? 'Their document'} was deleted 90 days after the request closed.</p>
          ) : null}
          {r.link ? (
            <p>
              <strong>Link:</strong>{' '}
              <a href={r.link} target="_blank" rel="noreferrer">
                {r.link}
              </a>
            </p>
          ) : null}
          {r.structure?.areas.length ? (
            <div className="req-typed">
              <p className="eyebrow">Typed in by the leader</p>
              {r.structure.areas.map((a, ai) => (
                <div key={ai} className="build-area">
                  <p className="build-area-name">{a.name}</p>
                  {a.indicators.map((i, ii) => (
                    <div key={ii} className="build-indicator">
                      <p className="build-indicator-name">{i.name}</p>
                      {i.description ? <p>{i.description}</p> : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          {r.details ? <div className="req-details">{r.details}</div> : null}
        </div>
      </details>

      {r.frameworkId && r.frameworkId !== r.build?.frameworkId ? (
        <p>
          <strong>Framework:</strong> <Link to={`/admin/frameworks/${r.frameworkId}`}>{r.frameworkName ?? 'Open it'}</Link>
        </p>
      ) : null}

      {closed ? (
        r.status === 'declined' && r.adminNote ? (
          <p className="muted">Declined: {r.adminNote}</p>
        ) : null
      ) : (
        <details className="req-fold">
          <summary>Other options</summary>
          <div className="req-fold-body">
            <div className="form-actions">
              <Button variant="secondary" onClick={buildYourself} disabled={busy}>
                Build it yourself in the editor
              </Button>
            </div>
            <div className="inline-form">
              <div className="field">
                <label htmlFor="req-fw">Or give them a published framework</label>
                <select id="req-fw" className="select" value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
                  <option value="">Choose a framework</option>
                  {published.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  act(() => updateRequest(r.id, { status: 'ready', frameworkId }), 'Ready. The organisation has access and the leader has been told.')
                }
                disabled={!frameworkId || busy}
              >
                Mark ready
              </Button>
            </div>
            {frameworkId ? <LeaderMessagePreview requestId={r.id} status="ready" who={r.requestedBy ?? 'the leader'} /> : null}
            <div className="field">
              <label htmlFor="req-note">Decline, with a note saying why</label>
              <textarea id="req-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
              {note.trim() ? <LeaderMessagePreview requestId={r.id} status="declined" note={note} who={r.requestedBy ?? 'the leader'} /> : null}
              <div className="form-actions">
                <Button
                  variant="ghost"
                  onClick={() => act(() => updateRequest(r.id, { status: 'declined', adminNote: note }), 'Declined. The leader has been told.')}
                  disabled={!note.trim() || busy}
                >
                  Decline
                </Button>
              </div>
            </div>
          </div>
        </details>
      )}
    </section>
  );
}
